import {
	type IssueLayoutOwner,
	IssueLayoutIssueTypeOwnerType,
	IssueLayoutRequestTypeOwnerType,
} from '../../types/issue/issue-layout-owner.tsx';
import type {
	JiraIssueLayoutOwnerFragment,
	JiraIssueLayoutOwnerFragment_JiraIssueLayoutIssueTypeOwner as GqlIssueLayoutIssueTypeOwner,
	JiraIssueLayoutOwnerFragment_JiraIssueLayoutRequestTypeOwner as GqlIssueLayoutRequestTypeOwner,
} from '../fragments/__generated_apollo__/JiraIssueLayoutOwnerFragment';

export const ASSOCIATED_SCREEN_ID_REQUEST_OWNER_PROPERTY_KEY =
	'jsm.issue.type.associated.screen.id';
export const ID_REQUEST_OWNER_PROPERTY_KEY = 'jsm.issue.type.id';
export const NAME_REQUEST_OWNER_PROPERTY_KEY = 'jsm.issue.type.name';
export const ICON_ID_REQUEST_OWNER_PROPERTY_KEY = 'jsm.issue.type.icon.id';
export const ICON_URL_REQUEST_OWNER_PROPERTY_KEY = 'jsm.issue.type.icon.url';

function issueTypeOwner({
	id,
	name,
	description,
	avatarId,
	iconUrl,
}: GqlIssueLayoutIssueTypeOwner) {
	return {
		type: IssueLayoutIssueTypeOwnerType,
		data: {
			// Ensure to update IssueTypeOwnerDataBean in Jira BE if new fields added as this is used by both load AND save flow.
			// Addition of new field here must be feature flagged otherwise it may cause an incident (e.g. HOT-110012).
			id: parseInt(id, 10),
			name,
			description: description || '',
			avatarId: avatarId ?? null,
			iconUrl: iconUrl ?? null,
		},
	};
}

function requestTypeOwner({
	id,
	externalUuid,
	name,
	description,
	avatarId,
	iconUrl,
	instructions,
	restrictionStatus,
	properties,
}: GqlIssueLayoutRequestTypeOwner) {
	return {
		type: IssueLayoutRequestTypeOwnerType,
		data: {
			// Ensure to update RequestTypeOwnerDataBean in Jira BE if new fields added as this is used by both load AND save flow.
			// Addition of new field here must be feature flagged otherwise it may cause an incident (e.g. HOT-110012).
			id: parseInt(id, 10),
			name,
			description: description || '',
			avatarId: avatarId ?? null,
			iconUrl: iconUrl ?? null,
			instructions: instructions || '',
			externalUuid,
			restrictionStatus,
			properties: {
				[ASSOCIATED_SCREEN_ID_REQUEST_OWNER_PROPERTY_KEY]:
					properties?.[ASSOCIATED_SCREEN_ID_REQUEST_OWNER_PROPERTY_KEY],
				[NAME_REQUEST_OWNER_PROPERTY_KEY]: properties?.[NAME_REQUEST_OWNER_PROPERTY_KEY],
				[ICON_ID_REQUEST_OWNER_PROPERTY_KEY]: properties?.[ICON_ID_REQUEST_OWNER_PROPERTY_KEY],
				[ICON_URL_REQUEST_OWNER_PROPERTY_KEY]: properties?.[ICON_URL_REQUEST_OWNER_PROPERTY_KEY],
				[ID_REQUEST_OWNER_PROPERTY_KEY]: properties?.[ID_REQUEST_OWNER_PROPERTY_KEY],
			},
		},
	};
}

export const transformLayoutOwners = (
	gqlLayoutOwners: ReadonlyArray<JiraIssueLayoutOwnerFragment>,
) => {
	const result: IssueLayoutOwner[] = [];
	for (let i = 0; i < gqlLayoutOwners.length; i += 1) {
		const item = gqlLayoutOwners[i];
		if (item.__typename === 'JiraIssueLayoutIssueTypeOwner') {
			result.push(issueTypeOwner(item));
		}
		if (item.__typename === 'JiraIssueLayoutRequestTypeOwner') {
			result.push(requestTypeOwner(item));
		}
	}

	return result;
};
